import { format, getMonth, getYear } from 'date-fns';
import {
    getMonthlyFormatted,
    getQuarterFormatted,
    getWeeklyFormatted,
    quarterNumber,
    formattedQuarterWithYear,
    formattedWeekWithMonthAndYear,
    getTwoDigitDayOfMonth,
    formatPercent,
} from '../../agent-production/helpers/dataFormatters';
import { barChartColors, marketDynamicsColors, periodKeys, terms } from '../constants';
import { tabIndex } from '../constants/listingViews/overview';
import { isFunction } from 'lodash-es';
import { marketDynamicsTerms as mdt, marketDynamicsTimePeriods as mdtp } from '../../constants';
import { cardKeyFactors as ckf, formatterByTimePeriod } from '../constants/dashboards';
import { pdfChartLabelStyles, pdfChartTitleStyles } from '../constants/pdf';

const STACKED_BAR_WIDTH = 2;

export const getDataPoints = (checkedStatusOptions, data) => {
    const radioRemoved = checkedStatusOptions.filter(x => !x.isRadio);
    return radioRemoved.map(({ keyField }) => ({
        data: data?.map((item) => item[keyField] ?? 0) ?? []
    }));
};

export const getMonthlyCategories = (data) =>
    data?.map((item, index) => {
        const date = new Date(item.timePeriodStart);
        const month = format(date, 'MM');
        const shouldRenderYear = index === 0 || index === data.length - 1 || month === '01';
        return item && getMonthlyFormatted(date, shouldRenderYear);
    }) ?? [];

export const getQuarterlyCategories = (data) =>
    data?.map(
        (item, index) =>
            item &&
            getQuarterFormatted(
                item.timePeriodStart,
                getIsFirstOrLastItem(index, data) ? formattedQuarterWithYear : quarterNumber
            )
    ) ?? [];

export const getFormattedDateObj = (date) => ({
    dayOfMonth: getTwoDigitDayOfMonth(date),
    month: getMonth(date),
    year: getYear(date),
    formattedMonth: format(date, 'MMM'),
    formattedYear: format(date, 'yy')
});

export const getIsFirstOrLastItem = (index, data) => index === 0 || index === data.length - 1;

export const getWeeklyCategories = (data) =>
    data?.map((item, index) => {
        const prevDate = index !== 0 && new Date(data[index - 1].timePeriodStart);
        const date = new Date(item.timePeriodStart);

        const isFirstOrLastItem = getIsFirstOrLastItem(index, data);

        const dateObject = getFormattedDateObj(date);
        const { month: prevMonth, year: prevYear } = prevDate && getFormattedDateObj(prevDate);

        const hasChangedMonth = dateObject.month !== prevMonth;
        const hasChangedYear = dateObject.year !== prevYear;
        const shouldShowYear = hasChangedYear || isFirstOrLastItem;

        if (isFirstOrLastItem || hasChangedMonth)
            return formattedWeekWithMonthAndYear(dateObject, shouldShowYear);
        return item && getWeeklyFormatted(date);
    }) ?? [];

export const getBeginVsEndMonthColors = (dataPointIndex, seriesIndex, w, selectedPeriodComparison,
    selectedBeginIndex, selectedEndIndex, intervalType, isOverview, timePeriod) => {

    const is6MonthsSearch = mdtp[mdt.months6Weekly] === timePeriod;
    if (isOverview || is6MonthsSearch) return marketDynamicsColors.bars[seriesIndex];

    const data = w.config.series[0].data;
    if (intervalType === terms.weekOf) {
        if (getIsFirstOrLastItem(dataPointIndex, data)) return marketDynamicsColors.bars[seriesIndex];
        return marketDynamicsColors.barsTransparency[seriesIndex];
    }
    if (selectedPeriodComparison === periodKeys.wholeTimePeriod) {
        if (dataPointIndex >= selectedBeginIndex && dataPointIndex <= selectedEndIndex)
            return marketDynamicsColors.bars[seriesIndex];

        return marketDynamicsColors.barsTransparency[seriesIndex];
    }

    if (dataPointIndex === selectedBeginIndex || dataPointIndex === selectedEndIndex)
        return marketDynamicsColors.bars[seriesIndex];

    return marketDynamicsColors.barsTransparency[seriesIndex];
};

export const getBarWidth = ({ barCount, chartRowCount, isStacked }) => {
    if (isStacked) return formatPercent(chartRowCount * STACKED_BAR_WIDTH);
    return formatPercent(chartRowCount * barCount);
};

export const handleDataPointSelection = (handleBarClick) => (config) => {
    handleBarClick(config.dataPointIndex);
};

export const handleDataPointMouseEnter = (dataPointMouseOverStyler) => (event, chartContext, config) => {
    dataPointMouseOverStyler(event, chartContext, config);
};

export const calculateColumnWidth = ({ checkedStatusOptionsLength, dataLength, getBarWidth, isStacked }) => {
    return getBarWidth({barCount: checkedStatusOptionsLength, chartRowCount: dataLength, isStacked});
};

export const getCategories = ({config, data}) => config?.groupConfig?.chart?.xaxis?.categories(data) ?? [];

export const getFillColors = ({ getBeginVsEndMonthColors, highHexColor, intervalType, isOverview, lowHexColor,
    selectedBeginIndex, selectedEndIndex, selectedPeriodComparison, threshold, overviewStatus, timePeriod }) => {
        if (isOverview && overviewStatus.currentTab === tabIndex.inventory)
            return [({ value }) => value < threshold ? lowHexColor : highHexColor];
        return [({ dataPointIndex, seriesIndex, w }) => getBeginVsEndMonthColors(dataPointIndex, seriesIndex, w,
            selectedPeriodComparison, selectedBeginIndex, selectedEndIndex, intervalType, isOverview, timePeriod)];
};

export const customizeTooltip = ({ config, data, getChartTooltipContent,marketDynamicsColors }) => ({ series, dataPointIndex }) => (
    getChartTooltipContent(data, series, dataPointIndex, marketDynamicsColors.bars, config)
);

export const getOptions = ({ chartLabelStyles, checkedStatusOptions, config, data, dataPointMouseOverStyler,
    fontFamily, getBeginVsEndMonthColors, getChartTooltipContent, handleBarClick, handleDataPointMouseEnter,
    handleDataPointSelection, highHexColor, intervalType, isOverview, isStacked, lowHexColor, overviewStatus,
    selectedBeginIndex, selectedEndIndex, selectedPeriodComparison, threshold, xAxisLegends, auxHexColor, isPDFChart, timePeriod }) => ({
        chart: {
            animations: {
                enabled: !isPDFChart,
            },
            borderRadiusWhenStacked: 'last',
            fontFamily,
            toolbar: { show: false },
            stacked: isStacked,
            events: {
                dataPointSelection: (event, chartContext, config) => handleDataPointSelection(handleBarClick)(config),
                dataPointMouseEnter: handleDataPointMouseEnter(dataPointMouseOverStyler),
            },
        },
        dataLabels: { enabled: false },
        plotOptions: {
            bar: {
                horizontal: false,
                borderRaidusApplication: 'end',
                borderRadius: 3,
                columnWidth: calculateColumnWidth({checkedStatusOptionsLength: checkedStatusOptions?.length,
                    dataLength: data?.length ?? [], getBarWidth, isStacked})
            }
        },
        xaxis: {
            categories: getCategories({config, data}),
            labels: { style: isPDFChart ? pdfChartLabelStyles : chartLabelStyles },
        },
        yaxis: {
            ...config?.listingConfig?.chart?.yAxis,
            labels: {
                ...config?.listingConfig?.chart?.yAxis?.labels,
                style: isPDFChart ? pdfChartLabelStyles : chartLabelStyles,
            },
            title: {
                ...config?.listingConfig?.chart?.yAxis?.title,
                style: isPDFChart ? pdfChartTitleStyles : '',
            }
        },
        fill: {
            colors: getFillColors({isOverview, threshold, lowHexColor, highHexColor, getBeginVsEndMonthColors,
                overviewStatus, selectedPeriodComparison, selectedBeginIndex, selectedEndIndex, intervalType, timePeriod })
        },
        legend: {
            show: isOverview,
            inverseOrder: false,
            customLegendItems: xAxisLegends || [],
            markers: {
                fillColors: [
                    lowHexColor,
                    highHexColor,
                    ...(overviewStatus.currentTab !== tabIndex.inventory ? [auxHexColor] : [])
                ],
                width: 15,
                height: 15,
                radius: 10
            },
            fontFamily,
            itemMargin: { horizontal: 10 },
            onItemClick: { toggleDataSeries: false },
            position: 'bottom',
            horizontalAlign: 'center',
            showForSingleSeries: true,
            labels: { colors: '#4B5563' },
            ...(isPDFChart ? {fontSize: '16px'} : {})
        },
        stroke: {
            colors: ['transparent'],
            width: 5
        },
        tooltip: {
            custom: customizeTooltip({data, marketDynamicsColors, config, getChartTooltipContent})
        },
        grid: { yaxis: { lines: { show: false } } }
    });

// Hack to calculate the gap until we have a better way.
export const getTextWithGap = (text, keyFactor) => {

    const firefoxGap = (navigator?.userAgent?.toLowerCase()?.includes('firefox') ? 1.18 : 1) * keyFactor;

    // Hack: values in the regexps contains english so they are not localized.
    const patterns = [
        { regex: /^\$\d{3}\.\d[kMB]/, multiplier: 4.8 }, // e.g: $123.4M
        { regex: /^\$\d{2}\.\d[kMB]/, multiplier: 5.5 }, // e.g: $12.3k
        { regex: /^\$\d{1}\.\d[kMB]/, multiplier: 5.5 }, // e.g: $1.2B
        { regex: /^\$\d{3}[kMB]/, multiplier: 5.5 }, // e.g: $123M
        { regex: /^\$\d{2}[kMB]/, multiplier: 5.5 }, // e.g: $12M
        { regex: /\$\d{3} /, multiplier: 6.2 }, // e.g: $123
        { regex: /\$\d{2} /, multiplier: 6 }, // e.g: $12
        { regex: /^\d{2}\.\d\%/, multiplier: 5.3 }, // e.g: 12.3%
        { regex: /^\d{3}\.\d\%/, multiplier: 4.9 }, // e.g: 123.4%
        { regex: /^\d{3}\.\d{1} $/, multiplier: 5 }, // e.g: 123.4
        { regex: /^\d{2}\.\d{1} $/, multiplier: 5.2 }, // e.g: 12.3
        { regex: /^\d{1}\.\d{1} $/, multiplier: 5.7 }, // e.g: 1.2
        { regex: /^\d{3},\d{3}\.\d /, multiplier: 4.4 }, // e.g: 123,456.7
        { regex: /^\d{2},\d{3}\.\d /, multiplier: 4.5 }, // e.g: 12,345.6
        { regex: /^\d{1},\d{3}\.\d /, multiplier: 4.5 }, // e.g: 1,234.5
        { regex: /^\d{3} Days/, multiplier: 5 }, // e.g: 123 Days
        { regex: /^\d{2} Days/, multiplier: 4.9 }, // e.g: 12 Days
        { regex: /^\d{1} Days/, multiplier: 5.4 }, // e.g: 1 Days
        { regex: /^\d{2}\.\d Months/, multiplier: 4.4 }, // e.g: 12.3 Months
        { regex: /^\d{1}\.\d Months/, multiplier: 4.5 }, // e.g: 1.2 Months
    ];

    for (const { regex, multiplier } of patterns) {
        if (regex.test(text)) return text.padStart(text.length * multiplier * firefoxGap, ' \u00A0');
    }
    return text;
};

export const getDashboardChartOptions = (chartData, card, timePeriod) => {
    const {
        formatter,
        labelUnitType = '',
        keyField,
        chartFormatter = formatter,
        chartUnitType = labelUnitType,
        chartDataFormatter,
    } = card;
    const beginningPeriodData = chartData[0]?.[keyField] ?? 0;
    const endPeriodData = chartData[1]?.[keyField] ?? 0;
    const formattedBeginningPeriodData = isFunction(chartDataFormatter)
        ? chartDataFormatter(beginningPeriodData)
        : beginningPeriodData;
    const formattedEndPeriodData = isFunction(chartDataFormatter)
        ? chartDataFormatter(endPeriodData)
        : endPeriodData;
    return {
        series: [
            {
                data: [formattedBeginningPeriodData, formattedEndPeriodData]
            }
        ],
        height: 110,
        colors: [barChartColors.first, barChartColors.second],
        options: {
            grid: {
                show: false,
                yaxis: { lines: { show: false } },
                xaxis: { lines: { show: false } },
                padding: { left: -20, top: -40, bottom: 0, right: 100 }
            },
            colors: [barChartColors.first, barChartColors.second],
            chart: {
                height: 50,
                background: barChartColors.pureWhite,
                animations: { enabled: false },
                type: 'bar',
                stacked: true,
                toolbar: { show: false },
                zoom: { enabled: false },
            },
            responsive: [
                { breakpoint: 480, options: { legend: { position: 'left', offsetX: -10, offsetY: 0 } } }
            ],
            plotOptions: {
                bar: {
                    horizontal: true,
                    barHeight: '20%',
                    borderRadiusApplication: 'end',
                    borderRadius: 3,
                    columnWidth: '30%',
                    dataLabels: { position: 'top' },
                    distributed: true,
                }
            },
            dataLabels: {
                enabled: true,
                style: { colors: [barChartColors.grayShadow] },
                formatter: (value) =>  getTextWithGap(`${chartFormatter(value)} ${chartUnitType}`, ckf[keyField]),
            },
            xaxis: {
                labels: { show: false },
                axisBorder: { show: false },
                axisTicks: { show: false }
            },
            yaxis: { labels: { show: false }, axisBorder: { show: false } },
            legend: {
                inverseOrder: false,
                customLegendItems: [
                    formatterByTimePeriod[timePeriod]?.(chartData[0]?.timePeriodStart).toUpperCase(),
                    formatterByTimePeriod[timePeriod]?.(chartData[1]?.timePeriodStart).toUpperCase()
                ],
                markers: {
                    width: 15,
                    height: 15,
                    radius: 10,
                    fillColors: [barChartColors.first, barChartColors.second]
                },
                itemMargin: { horizontal: 10 },
                onItemClick: { toggleDataSeries: false },
                position: 'bottom',
                horizontalAlign: 'left',
                showForSingleSeries: true,
                fontSize: '12px',
                offsetY: -20,
                offsetX: -40
            },
            fill: { opacity: 1, colors: [barChartColors.first, barChartColors.second] },
            states: { hover: { filter: 'none' } },
            tooltip: { enabled: false }
        }
    };
};

export const getPdfDashboardChartOptions = (chartData, card, timePeriod, isInventory) => {
    const {
        formatter,
        labelUnitType = '',
        keyField,
        chartFormatter = formatter,
        chartUnitType = labelUnitType,
        chartDataFormatter,
    } = card;
    const beginningPeriodData = chartData[0]?.[keyField] ?? 0;
    const endPeriodData = chartData[1]?.[keyField] ?? 0;
    const formattedBeginningPeriodData = isFunction(chartDataFormatter)
        ? chartDataFormatter(beginningPeriodData)
        : beginningPeriodData;
    const formattedEndPeriodData = isFunction(chartDataFormatter)
        ? chartDataFormatter(endPeriodData)
        : endPeriodData;
    return {
        series: [
            {
                data: [formattedBeginningPeriodData, formattedEndPeriodData]
            }
        ],
        height: isInventory ? 350 : 110,
        colors: [barChartColors.first, barChartColors.second],
        options: {
            grid: {
                show: false,
                yaxis: { lines: { show: false } },
                xaxis: { lines: { show: false } },
                padding: { left: -20, top: -40, bottom: 0, right: 100 }
            },
            colors: [barChartColors.first, barChartColors.second],
            chart: {
                height: 50,
                background: barChartColors.pureWhite,
                animations: { enabled: false },
                type: 'bar',
                stacked: true,
                toolbar: { show: false },
                zoom: { enabled: false },
            },
            responsive: [
                { breakpoint: 480, options: { legend: { position: 'left', offsetX: -10, offsetY: 0 } } }
            ],
            plotOptions: {
                bar: {
                    horizontal: true,
                    barHeight: isInventory ? '10%' : '20%',
                    borderRadiusApplication: 'end',
                    borderRadius: 3,
                    columnWidth: '30%',
                    dataLabels: { position: 'top' },
                    distributed: true,
                }
            },
            dataLabels: {
                enabled: true,
                style: { colors: [barChartColors.grayShadow], fontSize: '18px' },
                formatter: (value) =>  getTextWithGap(`${chartFormatter(value)} ${chartUnitType}`, ckf[keyField]),
            },
            xaxis: {
                labels: { show: false },
                axisBorder: { show: false },
                axisTicks: { show: false }
            },
            yaxis: { labels: { show: false }, axisBorder: { show: false } },
            legend: {
                inverseOrder: false,
                customLegendItems: [
                    formatterByTimePeriod[timePeriod]?.(chartData[0]?.timePeriodStart).toUpperCase(),
                    formatterByTimePeriod[timePeriod]?.(chartData[1]?.timePeriodStart).toUpperCase()
                ],
                markers: {
                    width: 15,
                    height: 15,
                    radius: 10,
                    fillColors: [barChartColors.first, barChartColors.second]
                },
                itemMargin: { horizontal: 10 },
                onItemClick: { toggleDataSeries: false },
                position: 'bottom',
                horizontalAlign: 'left',
                showForSingleSeries: true,
                fontSize: '20px',
                offsetY: -20,
                offsetX: -40
            },
            fill: { opacity: 1, colors: [barChartColors.first, barChartColors.second] },
            states: { hover: { filter: 'none' } },
            tooltip: { enabled: false }
        }
    };
};
